<script setup lang="ts">
import { computed, ref, toRef, watch } from "vue";
import { useTranslation } from "i18next-vue";
import type { FieldObservation } from "@/types/field-observations.types";
import PartsTreePanel from "./PartsTreePanel.vue";
import PartObservationRow from "./PartObservationRow.vue";
import { useGetFieldParts } from "@/stores/queries/field-observations.query";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";
import { countByPart } from "@/utils/observation-plan";

/**
 * Contenu de l'onglet Parties : l'arborescence des parties de l'ouvrage
 * (ajout, renommage, modèles — même brique que le sélecteur de partie), avec
 * le nombre d'observations rattachées à chacune. Sélectionner une partie
 * ouvre le MODE ASSOCIATION : la barre basse du canvas (`PartAssignBar`)
 * prend la place de la barre de sélection — toucher le badge d'une
 * observation sur le plan la rattache (ou la détache), « Sélectionner une
 * zone » rattache d'un trait toutes celles d'un rectangle. Les observations
 * de la partie sélectionnée sont listées sous sa ligne. Les actions d'ajout
 * (partie racine, modèle) sont exposées pour le pied statique du volet.
 */

const props = defineProps<{
  diagnosticId: number;
  /** Observations de l'ouvrage, de la première à la dernière créée. */
  observations: FieldObservation[];
  /** Lettre de chaque observation, par id. */
  letters: Map<number, string>;
  /** Partie d'une observation, choix en cours d'enregistrement compris. */
  partIdOf: (observation: FieldObservation) => number | null;
  /** Observations (par id) qui ont des éléments sur ce plan. */
  onPlanIds: Set<number>;
  /** Observation mise en avant sur le plan. */
  currentId: number | null;
  /** Hors ligne : le classement est impossible. */
  disabled: boolean;
}>();

const emit = defineEmits<{
  (e: "locate", observation: FieldObservation): void;
  /** Aller classer depuis les fiches (liste triée par partie). */
  (e: "open-list"): void;
}>();

const { t } = useTranslation();
const state = usePlanAnalysisState();
const { data: parts } = useGetFieldParts(toRef(props, "diagnosticId"));

const counts = computed(() => countByPart(props.observations, props.partIdOf));

const unassigned = computed(() =>
  props.observations.filter(
    (observation) => props.partIdOf(observation) === null,
  ),
);

/**
 * Sans partie ET sans élément sur ce plan : aucun badge à toucher ici, elles
 * se classent depuis leur fiche.
 */
const unassignedOffPlanCount = computed(
  () =>
    unassigned.value.filter(
      (observation) => !props.onPlanIds.has(observation.id),
    ).length,
);

const observationsOf = (partId: number): FieldObservation[] =>
  props.observations.filter(
    (observation) => props.partIdOf(observation) === partId,
  );

/** Sélectionner la partie déjà sélectionnée la désélectionne (fin du mode). */
const handleSelect = (partId: number | undefined): void => {
  if (props.disabled) return;
  state.setAssignPart(
    partId === undefined || partId === state.assignPartId ? null : partId,
    props.diagnosticId,
  );
};

const panel = ref<InstanceType<typeof PartsTreePanel> | null>(null);
defineExpose({
  addRootPart: (): void => panel.value?.addRoot(),
  insertTemplate: (): void => panel.value?.openTemplate(),
});

// Une partie supprimée côté serveur ne peut plus être la cible.
watch(parts, (list) => {
  if (
    state.assignPartId !== null &&
    list &&
    !list.some((part) => part.id === state.assignPartId)
  ) {
    state.setAssignPart(null);
  }
});

// La connexion tombe : on ne laisse pas un mode dont aucun geste n'aboutit.
watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) state.setAssignPart(null);
  },
);
</script>

<template>
  <div class="parts-plan">
    <div class="d-flex align-items-start gap-2 mb-2">
      <div class="parts-hint flex-grow-1">
        {{ t("canvas:observations.parts.hint") }}
        <span v-if="unassigned.length" class="unassigned">
          ·
          {{
            t("canvas:observations.parts.unassigned", {
              count: unassigned.length,
            })
          }}
        </span>
      </div>
      <!-- Outillage en bout de ligne (développement) -->
      <slot name="tools" />
    </div>

    <!-- Sans partie et hors plan : pas de badge à toucher, direction la liste -->
    <div
      v-if="unassignedOffPlanCount"
      class="off-plan-note d-flex align-items-center gap-2 mb-2"
    >
      <span class="flex-grow-1">
        {{
          t("canvas:observations.parts.unassignedOffPlan", {
            count: unassignedOffPlanCount,
          })
        }}
      </span>
      <button
        type="button"
        class="open-list-btn flex-shrink-0"
        @click="emit('open-list')"
      >
        {{ t("canvas:observations.parts.openList") }}
      </button>
    </div>

    <PartsTreePanel
      ref="panel"
      :diagnostic-id="diagnosticId"
      :model-value="state.assignPartId ?? undefined"
      :counts="counts"
      inline
      @update:model-value="handleSelect"
    >
      <template #after-row="{ part }">
        <div
          v-if="part.id === state.assignPartId"
          class="part-observations d-flex flex-column"
        >
          <PartObservationRow
            v-for="observation in observationsOf(part.id)"
            :key="observation.id"
            :observation="observation"
            :letter="letters.get(observation.id) ?? ''"
            :on-plan="onPlanIds.has(observation.id)"
            :current="currentId === observation.id"
            @select="emit('locate', observation)"
          />
          <div v-if="observationsOf(part.id).length === 0" class="part-empty">
            {{ t("canvas:observations.parts.noObservation") }}
          </div>
        </div>
      </template>
    </PartsTreePanel>
  </div>
</template>

<style lang="scss" scoped>
.parts-hint {
  font-size: 0.78rem;
  opacity: 0.7;

  .unassigned {
    color: var(--bs-warning);
  }
}

.off-plan-note {
  font-size: 0.76rem;
  border: 1px solid var(--canvas-border);
  border-radius: 6px;
  padding: 6px 8px;
  color: rgba(255, 255, 255, 0.75);
}

.open-list-btn {
  border: none;
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.74rem;
  padding: 3px 8px;
  white-space: nowrap;

  &:hover {
    background-color: rgba(255, 255, 255, 0.18);
    color: #fff;
  }
}

.part-observations {
  margin: 2px 0 6px 56px;
  border-left: 2px solid color-mix(in srgb, var(--bs-primary) 55%, transparent);
  padding-left: 8px;
}

.part-empty {
  font-size: 0.76rem;
  opacity: 0.55;
  padding: 4px 6px;
}
</style>
